/**
 * NewHouseDialog — V14.62 Sprint 22 (Mode Maison Unifamiliale).
 *
 * Simple modale de création d'une maison. Reste minimaliste : nom, client
 * optionnel, adresse optionnelle. Les 4 étages par défaut sont créés
 * côté backend. L'utilisateur ajustera les étages dans l'éditeur.
 */
import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Home, X, Loader2 } from 'lucide-react';
import { formatApiError } from '../../lib/formatApiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewHouseDialog = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [clientName, setClientName] = useState('');
  const [address, setAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const reset = () => { setName(''); setClientName(''); setAddress(''); };
  const cancel = () => { reset(); onClose?.(); };

  const submit = async () => {
    if (!name.trim()) {
      toast.error('Donnez un nom à la maison');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/single-house/projects`,
        {
          name: name.trim(),
          client_name: clientName.trim() || null,
          address: address.trim() || null,
        },
        { withCredentials: true },
      );
      toast.success(`Maison « ${data.name} » créée`);
      reset();
      onClose?.();
      navigate(`/maison/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err, 'Création impossible'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[2100] flex items-center justify-center backdrop-blur-sm"
      style={{ background: 'rgba(60, 60, 70, 0.35)' }}
      onKeyDown={(e) => { if (e.key === 'Escape') cancel(); }}
      tabIndex={-1}
      ref={(el) => el && !el.contains(document.activeElement) && el.focus()}
      data-testid="new-house-dialog"
      role="dialog" aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) cancel(); }}
    >
      <div className="hud-page-container !min-h-0 w-[460px] max-w-[92vw] rounded-2xl border overflow-hidden shadow-2xl"
           style={{ background: 'var(--c-surface-0)', borderColor: 'var(--c-border-strong)' }}>
        <div className="flex items-center gap-2 px-4 py-3 border-b"
             style={{ borderColor: 'rgba(16,185,129,0.25)' }}>
          <Home className="w-4 h-4 text-emerald-400" />
          <h3 className="flex-1 text-sm font-semibold text-white">Nouvelle maison unifamiliale</h3>
          <button type="button" onClick={cancel} data-testid="new-house-close"
                  className="w-7 h-7 inline-flex items-center justify-center rounded text-gray-400 hover:text-white hover:bg-white/5">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 py-4 space-y-3">
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-[0.2em] text-emerald-300/80">
              Nom de la maison *
            </span>
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              autoFocus placeholder="Ex. Maison Flo X"
              className="mt-1 w-full px-3 py-2 rounded border bg-black/40 text-white text-[13px] focus:outline-none focus:border-emerald-400"
              style={{ borderColor: 'rgba(255,255,255,0.15)' }}
              data-testid="new-house-name-input"
            />
          </label>

          <label className="block">
            <span className="text-[10.5px] uppercase tracking-[0.2em] text-gray-400">
              Client (optionnel)
            </span>
            <input
              type="text" value={clientName} onChange={(e) => setClientName(e.target.value)}
              placeholder="Ex. Flo X"
              className="mt-1 w-full px-3 py-2 rounded border bg-black/40 text-white text-[13px] focus:outline-none focus:border-emerald-400"
              style={{ borderColor: 'rgba(255,255,255,0.15)' }}
              data-testid="new-house-client-input"
            />
          </label>

          <label className="block">
            <span className="text-[10.5px] uppercase tracking-[0.2em] text-gray-400">
              Adresse (optionnel)
            </span>
            <input
              type="text" value={address} onChange={(e) => setAddress(e.target.value)}
              placeholder="Ex. 12 rue de la Paix, 75001 Paris"
              className="mt-1 w-full px-3 py-2 rounded border bg-black/40 text-white text-[13px] focus:outline-none focus:border-emerald-400"
              style={{ borderColor: 'rgba(255,255,255,0.15)' }}
              data-testid="new-house-address-input"
            />
          </label>

          <p className="text-[10.5px] text-gray-500 leading-relaxed pt-1">
            4 étages par défaut seront créés (Sous-sol, RDC, R+1, Combles). Vous
            pourrez ensuite ajouter ou retirer des niveaux dans l&apos;éditeur.
          </p>
        </div>

        <div className="flex items-center gap-2 px-4 py-3 border-t bg-black/30"
             style={{ borderColor: 'rgba(16,185,129,0.18)' }}>
          <button type="button" onClick={cancel} data-testid="new-house-cancel"
                  disabled={submitting}
                  className="px-3 py-1.5 text-[11px] rounded border border-white/15 text-gray-300 hover:bg-white/5">
            Annuler
          </button>
          <div className="flex-1" />
          <button type="button" onClick={submit} disabled={submitting || !name.trim()}
                  data-testid="new-house-submit-btn"
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 text-[11px] rounded bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
            {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Créer la maison
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewHouseDialog;
